import type { Direction } from "../core/types";

export interface EntranceAuditDescriptor {
  readonly direction: Direction;
  readonly width: number;
  readonly height: number;
  readonly depth: number;
  readonly radialDistance: number;
  readonly scaleX: number;
  readonly scaleY: number;
  readonly scaleZ: number;
  readonly alpha: number;
  readonly enabled: boolean;
}

export interface ScreenshotSignature {
  readonly meanLuminance: number;
  readonly luminanceDeviation: number;
  readonly edgeDensity: number;
}

export interface LeakageAuditResult {
  readonly passed: boolean;
  readonly violations: readonly string[];
}

export function auditNeutralEntrances(
  entrances: readonly EntranceAuditDescriptor[],
  revealMeshCount: number,
): LeakageAuditResult {
  const violations: string[] = [];
  if (revealMeshCount !== 0) violations.push("reveal_root_not_empty_before_correct_answer");
  if (entrances.length !== 4) violations.push("expected_four_entrances");
  const reference = entrances[0];
  if (reference) {
    for (const entrance of entrances) {
      if (!entrance.enabled) violations.push(`${entrance.direction}_disabled_before_answer`);
      for (const key of ["width", "height", "depth", "radialDistance", "scaleX", "scaleY", "scaleZ", "alpha"] as const) {
        if (Math.abs(entrance[key] - reference[key]) > 0.001) {
          violations.push(`${entrance.direction}_${key}_differs`);
        }
      }
    }
  }
  return { passed: violations.length === 0, violations };
}

export class RevealSequenceAudit {
  private correctCommittedAt: number | null = null;
  private wrongCommittedAfterReset = false;

  reset(): void {
    this.correctCommittedAt = null;
    this.wrongCommittedAfterReset = false;
  }

  answerCommitted(correct: boolean, monotonicTimeMs: number): void {
    if (correct) {
      this.correctCommittedAt = monotonicTimeMs;
      this.wrongCommittedAfterReset = false;
    } else {
      this.wrongCommittedAfterReset = true;
    }
  }

  revealStarted(monotonicTimeMs: number, corridorDistanceMeters: number): LeakageAuditResult {
    const violations: string[] = [];
    if (this.correctCommittedAt === null) violations.push("reveal_without_correct_commit");
    if (this.wrongCommittedAfterReset && this.correctCommittedAt === null) violations.push("reveal_after_wrong_answer");
    if (this.correctCommittedAt !== null && monotonicTimeMs <= this.correctCommittedAt) violations.push("reveal_not_after_commit");
    if (corridorDistanceMeters < 2) violations.push("reveal_before_two_meters");
    return { passed: violations.length === 0, violations };
  }
}

export function analyzeScreenshotPixels(
  pixels: Uint8ClampedArray,
  width: number,
  height: number,
): ScreenshotSignature {
  if (width <= 0 || height <= 0 || pixels.length !== width * height * 4) {
    throw new Error("Screenshot pixel buffer dimensions are invalid");
  }
  const luminance = new Float64Array(width * height);
  let sum = 0;
  for (let index = 0; index < luminance.length; index += 1) {
    const offset = index * 4;
    const value = 0.2126 * (pixels[offset] ?? 0) + 0.7152 * (pixels[offset + 1] ?? 0) + 0.0722 * (pixels[offset + 2] ?? 0);
    luminance[index] = value;
    sum += value;
  }
  const mean = sum / luminance.length;
  let variance = 0;
  let edges = 0;
  let comparisons = 0;
  for (let y = 0; y < height; y += 1) {
    for (let x = 0; x < width; x += 1) {
      const index = y * width + x;
      const value = luminance[index] ?? 0;
      variance += (value - mean) ** 2;
      if (x + 1 < width) {
        edges += Math.abs(value - (luminance[index + 1] ?? 0)) > 18 ? 1 : 0;
        comparisons += 1;
      }
      if (y + 1 < height) {
        edges += Math.abs(value - (luminance[index + width] ?? 0)) > 18 ? 1 : 0;
        comparisons += 1;
      }
    }
  }
  return {
    meanLuminance: mean / 255,
    luminanceDeviation: Math.sqrt(variance / luminance.length) / 255,
    edgeDensity: comparisons === 0 ? 0 : edges / comparisons,
  };
}

export function auditScreenshotSignatures(signatures: readonly ScreenshotSignature[]): LeakageAuditResult {
  const violations: string[] = [];
  if (signatures.length !== 4) return { passed: false, violations: ["expected_four_entrance_screenshots"] };
  const meanValues = signatures.map((signature) => signature.meanLuminance);
  const deviationValues = signatures.map((signature) => signature.luminanceDeviation);
  const edgeValues = signatures.map((signature) => signature.edgeDensity);
  if (range(meanValues) > 0.12) violations.push("entrance_screenshot_brightness_differs");
  if (range(deviationValues) > 0.1) violations.push("entrance_screenshot_contrast_differs");
  if (range(edgeValues) > 0.08) violations.push("entrance_screenshot_outline_differs");
  return { passed: violations.length === 0, violations };
}

export async function analyzeScreenshotDataUrl(dataUrl: string): Promise<ScreenshotSignature> {
  const image = new Image();
  image.src = dataUrl;
  await image.decode();
  const canvas = document.createElement("canvas");
  canvas.width = image.naturalWidth;
  canvas.height = image.naturalHeight;
  const context = canvas.getContext("2d", { willReadFrequently: true });
  if (!context) throw new Error("Screenshot analysis canvas is unavailable");
  context.drawImage(image, 0, 0);
  const pixels = context.getImageData(0, 0, canvas.width, canvas.height).data;
  return analyzeScreenshotPixels(pixels, canvas.width, canvas.height);
}

function range(values: readonly number[]): number {
  return Math.max(...values) - Math.min(...values);
}
